import React, { useEffect, useState } from 'react';
import { Input } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { useTheme } from '../../../context/ThemeContext';
import { useAllItemsQuery } from './hooks/useCategoriesQuery';
import { useSubmitCategory } from './hooks/useCategoryMutations';
import ImageUploadField from '../../Items/ImageUploadField';
import ItemsPickerSection from './ItemsPickerSection';
import { CategoryFormModalProps, CategoryFormValues, CategoryItem } from './types';
import {
  CancelButton,
  FieldError,
  FormDivider,
  FormField,
  FormLabel,
  FormSectionTitle,
  FormSubmitButton,
  ModalFooter,
  ModalScrollContent,
  ModalWrapper,
  RequiredMark,
} from './styles';

const DEFAULT_VALUES: CategoryFormValues = {
  category: '',
};

const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
  open,
  onClose,
  editingCategory,
  onSuccess,
}) => {
  const { isDarkMode } = useTheme();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    defaultValues: editingCategory ? { category: editingCategory.category } : DEFAULT_VALUES,
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingPicture, setExistingPicture] = useState(editingCategory?.picture ?? '');
  const [selectedItems, setSelectedItems] = useState<CategoryItem[]>([]);

  const { data: allItems = [], isLoading: isLoadingItems } = useAllItemsQuery(open);
  const { mutate: submitCategory, isPending } = useSubmitCategory();

  useEffect(() => {
    if (!open || !editingCategory) return;
    reset({ category: editingCategory.category });
    setExistingPicture(editingCategory.picture ?? '');
  }, [editingCategory?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync the category's existing item ids against the full item list once
  // both are available, mirroring how the original modal pre-selected items.
  useEffect(() => {
    if (!editingCategory || allItems.length === 0) {
      if (!editingCategory) setSelectedItems([]);
      return;
    }
    const categoryItems = allItems.filter(
      (item) => item.id != null && editingCategory.items.includes(item.id.toString())
    );
    setSelectedItems(categoryItems);
  }, [editingCategory, allItems]);

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    setImageFile(null);
    setExistingPicture('');
    setSelectedItems([]);
    onClose();
  };

  const onSubmit = (values: CategoryFormValues) => {
    submitCategory(
      {
        categoryName: values.category,
        sequenceNumber: editingCategory?.sequence_number ?? 0,
        imageFile,
        existingPicture,
        selectedItems,
        editingCategory,
      },
      {
        onSuccess: () => {
          handleClose();
          onSuccess();
        },
      }
    );
  };

  return (
    <ModalWrapper>
      <ModalScrollContent>
        <FormSectionTitle $isDark={isDarkMode}>Basic Information</FormSectionTitle>

        <FormField>
          <FormLabel $isDark={isDarkMode}>
            Category Name <RequiredMark />
          </FormLabel>
          <Controller
            name="category"
            control={control}
            rules={{ required: 'Category name is required' }}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Enter category name"
                status={errors.category ? 'error' : ''}
                size="large"
              />
            )}
          />
          {errors.category && <FieldError>{errors.category.message}</FieldError>}
        </FormField>

        <FormDivider $isDark={isDarkMode} />

        <FormSectionTitle $isDark={isDarkMode}>Photo</FormSectionTitle>

        <FormField>
          <ImageUploadField
            imageFile={imageFile}
            existingPicture={existingPicture}
            isDarkMode={isDarkMode}
            onFileSelected={setImageFile}
            onDelete={() => {
              setImageFile(null);
              setExistingPicture('');
            }}
          />
        </FormField>

        <FormDivider $isDark={isDarkMode} />

        <FormSectionTitle $isDark={isDarkMode}>Items</FormSectionTitle>

        <FormField>
          <ItemsPickerSection
            allItems={allItems}
            selected={selectedItems}
            onChange={setSelectedItems}
            isDarkMode={isDarkMode}
            isLoading={isLoadingItems}
          />
        </FormField>
      </ModalScrollContent>

      <ModalFooter $isDark={isDarkMode}>
        <CancelButton size="large" onClick={handleClose}>
          Cancel
        </CancelButton>
        <FormSubmitButton
          type="primary"
          size="large"
          loading={isPending}
          onClick={handleSubmit(onSubmit)}
        >
          {editingCategory ? 'Update Category' : 'Create Category'}
        </FormSubmitButton>
      </ModalFooter>
    </ModalWrapper>
  );
};

export default CategoryFormModal;
